/**
 * usePlaceSearch hook provides place search integration with map animations.
 * Handles place search, results display, and smooth map transitions to results.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { extractPlacesFromResult } from "@/lib/searchResults";
import { create } from "@bufbuild/protobuf";
import { PlaceSearchSchema } from "@generated/proto/model";

import { MarkerService } from "../services/MarkerService";
import { PlaceSearchService } from "../services/PlaceSearchService";
import { calculateBounds } from "../utils/animations";
import {
  calculateDistance,
  calculateRadiusFromZoom,
} from "../utils/geoCalculations";

import type {
  GeoCornerBounds,
  MapViewport,
  Place,
  PlaceSearch,
  PlaceSearchActions,
  PlaceSearchMarker,
  PlaceSearchState,
  UseMapAnimationReturn
} from '../types';
interface UsePlaceSearchConfig {
  /** Current map viewport for location-aware searches */
  viewport: MapViewport
  /** Animation system for map transitions */
  animations?: UseMapAnimationReturn
  /** Marker service for displaying search results */
  markerService?: MarkerService
  /** Auto-clear results when viewport changes significantly */
  autoClearOnMove?: boolean
  /** Maximum number of results to display */
  maxResults?: number
}

/**
 * Comprehensive place search hook with map integration.
 * Provides search capabilities with automatic result markers and map animations.
 */
export function usePlaceSearch({
  viewport,
  animations,
  markerService,
  autoClearOnMove = true,
  maxResults = 20
}: UsePlaceSearchConfig) {
  const serviceRef = useRef<PlaceSearchService>()
  const lastViewportRef = useRef<MapViewport>(viewport)

  // Initialize service
  if (!serviceRef.current) {
    serviceRef.current = new PlaceSearchService()
  }
  const service = serviceRef.current

  // Search state
  const [state, setState] = useState<PlaceSearchState>({
    query: '',
    results: [],
    loading: false,
    error: null,
    metadata: undefined,
    resultMarkers: []
  })

  // Auto-clear results when viewport changes significantly
  useEffect(() => {
    if (!autoClearOnMove || state.results.length === 0) return

    const currentCenter = viewport.center
    const lastCenter = lastViewportRef.current.center

    // Check if user moved significantly (>5km or zoom changed by >3 levels)
    const distance = calculateDistance(currentCenter, lastCenter)
    const zoomDiff = Math.abs(viewport.zoom - lastViewportRef.current.zoom)

    if (distance > 5000 || zoomDiff > 3) {
      setState(prev => ({
        ...prev,
        results: [],
        resultMarkers: [],
        metadata: undefined
      }))

      // Clear markers from marker service
      if (markerService) {
        state.resultMarkers.forEach(marker => {
          markerService.removeMarker(marker.id)
        })
      }
    }

    lastViewportRef.current = viewport
  }, [viewport, autoClearOnMove, state.results.length, state.resultMarkers, markerService, service])

  // Create result markers from places
  const createResultMarkers = useCallback((places: Place[]): PlaceSearchMarker[] => {
    return service.createPlaceSearchMarkers(places)
  }, [service])

  // Update markers in marker service
  const updateResultMarkers = useCallback((markers: PlaceSearchMarker[]) => {
    if (!markerService) return

    // Remove old markers
    state.resultMarkers.forEach(marker => {
      markerService.removeMarker(marker.id)
    })

    // Add new markers
    markers.forEach(marker => {
      markerService.addMarker(marker)
    })
  }, [markerService, state.resultMarkers])

  // Search actions
  const actions = useMemo<PlaceSearchActions>(() => ({
    searchPlaces: async (search: PlaceSearch) => {
      setState(prev => ({ ...prev, loading: true, error: null }))

      try {
        const response = await service.searchPlaces(search)

        // Extract places from SearchSession results
        const places = extractPlacesFromResult(response)

        const markers = createResultMarkers(places.slice(0, maxResults))

        setState(prev => ({
          ...prev,
          results: places,
          resultMarkers: markers,
          metadata: response.searchSession?.metadata,
          loading: false,
          query: search.query || ''
        }))

        // Update marker service
        updateResultMarkers(markers)

        return response
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Search failed')
        setState(prev => ({
          ...prev,
          loading: false,
          error: err
        }))
        throw error
      }
    },

    getPlaceDetails: async (placeId: string) => {
      try {
        const response = await service.getPlaceDetails(placeId)
        return response
      } catch (error) {
        console.error('Failed to get place details:', error)
        throw error
      }
    },

    searchNearViewport: async (query: string) => {
      const search = create(PlaceSearchSchema, {
        query,
        location: viewport.center,
        radiusMeters: calculateRadiusFromZoom(viewport.zoom),
        maxResults
      })

      const places = await service.searchNearViewport(query, viewport, {
        maxResults
      })

      const markers = createResultMarkers(places)

      setState(prev => ({
        ...prev,
        results: places,
        resultMarkers: markers,
        query,
        loading: false,
        error: null
      }))

      updateResultMarkers(markers)
      return places
    },

    getPlacesInBounds: async (bounds: GeoCornerBounds) => {
      try {
        const places = await service.getPlacesInBounds(bounds, {
          maxResults
        })

        const markers = createResultMarkers(places)

        setState(prev => ({
          ...prev,
          results: places,
          resultMarkers: markers,
          loading: false,
          error: null
        }))

        updateResultMarkers(markers)
        return places
      } catch (error) {
        console.error('Failed to get places in bounds:', error)
        throw error
      }
    },

    clearResults: () => {
      // Clear markers from marker service
      if (markerService) {
        state.resultMarkers.forEach(marker => {
          markerService.removeMarker(marker.id)
        })
      }

      setState(prev => ({
        ...prev,
        results: [],
        resultMarkers: [],
        query: '',
        error: null,
        metadata: undefined
      }))
    }
  }), [service, viewport, maxResults, createResultMarkers, updateResultMarkers, markerService, state.resultMarkers])

  // Enhanced actions with map integration
  const mapIntegratedActions = {
    ...actions,

    /**
     * Search and automatically fit map to show all results.
     */
    searchAndShowResults: async (search: PlaceSearch) => {
      const response = await actions.searchPlaces(search)

      // Extract places from SearchSession results
      const places = extractPlacesFromResult(response)

      if (animations && places.length > 0) {
        const coordinates = places
          .filter(place => place.latlng)
          .map(place => place.latlng!)

        if (coordinates.length > 0) {
          const bounds = calculateBounds(coordinates)
          if (bounds) {
            await animations.animateToBounds(bounds, {
              duration: 1200,
              easing: 'ease-out',
              padding: 10
            })
          }
        }
      }

      return response
    },

    /**
     * Search near viewport and zoom to optimal level for results.
     */
    searchNearViewportAndFocus: async (query: string) => {
      const places = await actions.searchNearViewport(query)

      if (animations && places.length > 0) {
        const coordinates = places
          .filter(place => place.latlng)
          .map(place => place.latlng!)

        if (coordinates.length > 0) {
          const bounds = calculateBounds(coordinates)
          if (bounds) {
            await animations.animateToBounds(bounds, {
              duration: 1000,
              easing: 'ease-out',
              padding: 15
            })
          }
        }
      }

      return places
    },

    /**
     * Focus on a specific place with animation.
     */
    focusOnPlace: async (place: Place, zoom: number = 16) => {
      if (!place.latlng || !animations) return

      await animations.animateToViewport({
        center: place.latlng,
        zoom
      }, {
        duration: 800,
        easing: 'ease-out'
      })
    },

    /**
     * Get autocomplete suggestions based on current input.
     */
    getAutocompleteSuggestions: async (
      partialQuery: string,
      options?: { maxSuggestions?: number }
    ): Promise<string[]> => {
      try {
        return await service.getAutocompleteSuggestions(partialQuery, viewport, options)
      } catch (error) {
        console.warn('Autocomplete suggestions failed:', error)
        return []
      }
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear markers
      if (markerService) {
        state.resultMarkers.forEach(marker => {
          markerService.removeMarker(marker.id)
        })
      }
    }
  }, [])

  return {
    state,
    actions: mapIntegratedActions
  }
}

/**
 * Simplified hook for place search without map integration.
 * Useful for search components that don't control the map directly.
 */
export function useBasicPlaceSearch() {
  const serviceRef = useRef<PlaceSearchService>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  if (!serviceRef.current) {
    serviceRef.current = new PlaceSearchService()
  }

  const searchPlaces = useCallback(async (search: PlaceSearch) => {
    setLoading(true)
    setError(null)

    try {
      const response = await serviceRef.current!.searchPlaces(search)
      return response
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Search failed')
      setError(error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const getPlaceDetails = useCallback(async (placeId: string) => {
    try {
      return await serviceRef.current!.getPlaceDetails(placeId)
    } catch (error) {
      console.error('Failed to get place details:', error)
      throw error
    }
  }, [])

  return {
    searchPlaces,
    getPlaceDetails,
    loading,
    error
  }
}